import request from './axios';

/**
 * @typedef {object} Result
 * @property {object} AttachmentResourceGroups
 */

/**
 * Request for content page with ported query params from old urls.
 * Use single = true for one result, otherwise an array or results is returned.
 *
 * @param {object} options - see typedef
 * @param {object} options.attachmentResourceGroups -
 * @param {string} options.sortField - viable values: [pagetitle, owner, and status]. Otherwise ignored.
 * @param {boolean} [options.single=true] -
 * @param {number|string} [cat1Id=null] - Publications -> 9996, Meetings -> 9997, News Releases -> 9998
 * @returns {Result|Array<Result>} - Result(s) -- See typedef
 */
export default async function fetchContent(options, axiosConfig = {}) {
  const fetchOptionsKeys = Object.keys(options);
  const onlyFetchOptions =
    fetchOptionsKeys.length === 1 && fetchOptionsKeys[0] === 'fieldOptionsToReturn';

  const { single = false, fieldOptionsToReturn = [] } = options;

  const body = generatePostData(options);

  try {
    let searchResponse = { data: { Results: [] } };
    if (!onlyFetchOptions) {
      searchResponse = await request({
        url: `cms/whitepaperslibraryattachments`,
        method: 'POST',
        data: body,
        cache: true,
        cacheTime: 15,
        ...axiosConfig,
      });
    }
    const { MediaLibraryAttachmentsResults, Count } = searchResponse.data;

    const searchOptions = {};
    if (fieldOptionsToReturn.length) {
      const optionsResponse = await request('cms/whitepapersattachmentsoptions', {
        cache: true,
        cacheTime: 60 * 24,
        ...axiosConfig,
      });
      const { FieldOptions } = optionsResponse.data;
      for (let i = 0; i < fieldOptionsToReturn.length; i += 1) {
        const foKey = fieldOptionsToReturn[i];
        searchOptions[foKey] = FieldOptions[foKey];
      }
    }

    if (onlyFetchOptions) return { options: searchOptions };

    if (
      !MediaLibraryAttachmentsResults ||
      (Array.isArray(MediaLibraryAttachmentsResults) && !MediaLibraryAttachmentsResults.length)
    ) {
      throw new Error(
        `Page Data not found with options: ${Object.entries(options)
          .map(([key, val]) => `${key}: ${val}`)
          .join(' | ')}`
      );
    }

    return {
      results: single ? MediaLibraryAttachmentsResults[0] : MediaLibraryAttachmentsResults,
      options: searchOptions,
      cached: searchResponse.cached,
      count: Count,
    };
  } catch (error) {
    throw new Error(
      `Catch: Page Data not found with options: ${Object.entries(options)
        .map(([key, val]) => `${key}: ${val}`)
        .join(' | ')}`
    );
  }
}

export function generatePostData(options) {
  const {
    attachmentResourceGroups,
    sortField, // pagetitle, owner, and status (otherwise it defaults to publish date)
    sortOrder = null,
    pageSize = null,
    page = null,
  } = options;

  return JSON.stringify({
    FieldOptions: {
      AttachmentResourceGroups: attachmentResourceGroups,
      Page: page,
      PageSize: pageSize,
      SortOrder: sortOrder,
      SortField: sortField,
    },
  });
}
